// src/pages/clients/ClientAnalyticsService.js
import { api } from '../../services/api';
import { transformTimeSeriesData, transformDistributionData } from '../../utils/analyticsUtils';

/**
 * Gets client analytics data from the API and transforms it for display
 * @param {string} clientId - Client ID
 * @param {Object} params - Query parameters (startDate, endDate, etc.)
 * @returns {Object} Transformed analytics data
 */
export const getClientAnalytics = async (clientId, params = {}) => {
  try {
    // Call the API to get raw data
    const response = await api.get(`/analytics/clients/${clientId}`, { params });
    const data = response.data?.data?.analytics || {};
    
    // Transform the data for display
    return transformClientAnalyticsData(data);
  } catch (error) {
    console.error('Error fetching client analytics:', error);
    throw error;
  }
};

/**
 * Transforms client analytics data for display
 * @param {Object} data - Raw analytics data from API
 * @returns {Object} Transformed data
 */
export const transformClientAnalyticsData = (data) => {
  // Transform engagement trend data
  const engagementTrend = transformTimeSeriesData(
    data.engagementTrend || [],
    {
      dateKey: 'date',
      dateFormat: 'short',
      metrics: ['engagement', 'reach', 'impressions'],
      sortData: true
    }
  );
  
  // Transform platform breakdown data
  const platformBreakdown = transformDistributionData(
    data.platformBreakdown || {},
    {
      nameKey: 'name',
      valueKey: 'value',
      calculatePercent: true
    }
  );
  
  // Transform content performance data
  const contentPerformance = (data.contentPerformance || []).map(item => ({
    type: item.type,
    count: item.count || 0,
    engagement: item.engagement || 0,
    reach: item.reach || 0
  }));
  
  // Transform audience demographics data
  const audienceDemographics = {
    age: transformDistributionData(
      data.audienceDemographics?.age || [],
      {
        nameKey: 'name',
        valueKey: 'value',
        sortBy: 'name',
        sortDirection: 'asc'
      }
    ),
    gender: transformDistributionData(
      data.audienceDemographics?.gender || [],
      {
        nameKey: 'name',
        valueKey: 'value'
      }
    ),
    location: transformDistributionData(
      data.audienceDemographics?.location || [],
      {
        nameKey: 'name',
        valueKey: 'value',
        threshold: 2 // Combine locations with less than 2% into "Other"
      }
    )
  };
  
  // Create the final structure
  return {
    overview: data.overview || {
      followers: { value: 0, change: 0 },
      engagement: { value: 0, change: 0 },
      reach: { value: 0, change: 0 },
      impressions: { value: 0, change: 0 }
    },
    engagementTrend,
    contentPerformance,
    platformBreakdown,
    audienceDemographics,
    topContent: data.topContent || []
  };
};

/**
 * Export client analytics data
 * @param {string} clientId - Client ID
 * @param {Object} params - Query parameters
 * @returns {Blob} Data as blob for download
 */
export const exportClientAnalyticsData = async (clientId, params = {}) => {
  try {
    const response = await api.get(`/analytics/clients/${clientId}/export`, {
      params,
      responseType: 'blob'
    });
    
    return response.data;
  } catch (error) {
    console.error('Error exporting client analytics:', error);
    throw error;
  }
};

/**
 * Generates demo data for client analytics
 * @returns {Object} Demo analytics data
 */
export const generateDemoClientAnalytics = () => {
  return {
    overview: {
      followers: { value: 25800, change: 12.5 },
      engagement: { value: 3.2, change: 0.7 },
      reach: { value: 125000, change: 15.3 },
      impressions: { value: 458000, change: 8.2 },
    },
    engagementTrend: [
      { date: '2023-04-01', engagement: 2.8, reach: 21000, impressions: 45000 },
      { date: '2023-04-08', engagement: 3.1, reach: 23500, impressions: 51000 },
      { date: '2023-04-15', engagement: 2.9, reach: 22800, impressions: 48500 },
      { date: '2023-04-22', engagement: 3.4, reach: 25200, impressions: 54000 },
      { date: '2023-04-29', engagement: 3.2, reach: 24500, impressions: 52000 },
      { date: '2023-05-06', engagement: 3.8, reach: 28000, impressions: 59000 },
    ],
    contentPerformance: [
      { type: 'Photos', count: 32, engagement: 4.2, reach: 28500 },
      { type: 'Videos', count: 15, engagement: 5.8, reach: 42000 },
      { type: 'Carousels', count: 18, engagement: 6.3, reach: 35800 },
      { type: 'Stories', count: 45, engagement: 2.1, reach: 18900 },
    ],
    platformBreakdown: [
      { name: 'Instagram', value: 45 },
      { name: 'Facebook', value: 30 },
      { name: 'Twitter', value: 15 },
      { name: 'LinkedIn', value: 10 },
    ],
    audienceDemographics: {
      age: [
        { name: '13-17', value: 5 },
        { name: '18-24', value: 28 },
        { name: '25-34', value: 35 },
        { name: '35-44', value: 18 },
        { name: '45-54', value: 10 },
        { name: '55+', value: 4 },
      ],
      gender: [
        { name: 'Female', value: 58 },
        { name: 'Male', value: 40 },
        { name: 'Other', value: 2 },
      ],
      location: [
        { name: 'United States', value: 45 },
        { name: 'United Kingdom', value: 15 },
        { name: 'Canada', value: 12 },
        { name: 'Australia', value: 8 },
        { name: 'Germany', value: 6 },
        { name: 'France', value: 5 },
        { name: 'Other', value: 9 },
      ]
    },
    topContent: [
      {
        id: 'content1',
        title: 'Latest Product Announcement',
        type: 'Video',
        platform: 'Instagram',
        engagement: 7.8,
        reach: 56000,
        impressions: 78000,
        date: '2023-05-02'
      },
      {
        id: 'content2',
        title: 'Customer Success Story',
        type: 'Carousel',
        platform: 'LinkedIn',
        engagement: 6.3,
        reach: 35000,
        impressions: 42000,
        date: '2023-04-25'
      },
      {
        id: 'content3',
        title: 'Brand Campaign Launch',
        type: 'Photo',
        platform: 'Facebook',
        engagement: 5.9,
        reach: 48000,
        impressions: 62000,
        date: '2023-04-18'
      }
    ]
  };
};